import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Badge, Spinner, Alert, Form, InputGroup, FormControl, Pagination, Card, Row, Col } from 'react-bootstrap';
import { adminAPI, flaskServiceAPI } from '../../services/apiService';
import { getTranslatedError, translateError } from '../../utils/errorTranslator';
import { isMhtmlViewUrl, revokeViewBlobUrl } from '../../utils/mhtmlView';
import { DownloadOutlined, EyeOutlined, SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { debounce } from '../../utils/debounce';
import StackedModal, { getStackTopZIndex } from '../common/StackedModal';

const Modal = StackedModal;

const TasksList = ({ selectedUserId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewUrl, setViewUrl] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [zoomedShot, setZoomedShot] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewTitle, setViewTitle] = useState('Просмотр протокола');
  const [userFilterManuallyCleared, setUserFilterManuallyCleared] = useState(false);
  
  // Фильтры и пагинация
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    userId: selectedUserId || '',
    taskType: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Debounced функция для поиска
  const debouncedLoadTasks = useMemo(
    () => debounce(() => {
      loadTasks();
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Для поиска используем debounce, для остальных фильтров - сразу
    if (filters.search) {
      debouncedLoadTasks();
    } else {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters.status, filters.userId, filters.taskType, filters.sortBy, filters.sortOrder]);

  // Отдельный эффект для поиска с debounce
  useEffect(() => {
    if (filters.search !== undefined) {
      setPagination(prev => ({ ...prev, page: 1 }));
      if (filters.search) {
        debouncedLoadTasks();
      } else {
        loadTasks();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // Обновляем фильтр userId при изменении selectedUserId (только если фильтр не был сброшен вручную)
  useEffect(() => {
    if (selectedUserId) {
      if (!userFilterManuallyCleared) {
        setFilters(prev => ({ ...prev, userId: selectedUserId }));
        setPagination(prev => ({ ...prev, page: 1 }));
      }
      // Сбрасываем флаг при смене пользователя
      setUserFilterManuallyCleared(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.taskType && { taskType: filters.taskType }),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };
      
      const data = await adminAPI.getAllTasks(params);
      
      if (data.tasks) {
        setTasks(data.tasks);
      } else if (Array.isArray(data)) {
        setTasks(data);
      } else {
        setTasks([]);
      }
      
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total || 0,
          totalPages: data.pagination.pages || 0,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке задач: ' + getTranslatedError(err));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (task) => {
    try {
      const taskDetails = await adminAPI.getTaskDetails(task.id);
      setSelectedTask(taskDetails);
      setShowModal(true);
    } catch (err) {
      message.error('Ошибка при загрузке деталей задачи: ' + getTranslatedError(err));
    }
  };

  const buildAuthHeaders = () => {
    const accessToken = localStorage.getItem('accessToken');
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  };

  const fetchBlobWithAuth = async (url) => {
    const response = await fetch(url, { headers: buildAuthHeaders() });
    if (!response.ok) throw new Error('Не удалось загрузить контент');
    return response.blob();
  };

  const isSameOriginOrRelative = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch {
      return true;
    }
  };

  const handleViewProtocol = async (url, opts = {}) => {
    if (!url) return;
    try {
      setViewTitle(opts.title || 'Просмотр протокола');
      setViewLoading(true);
      setShowViewModal(true);
      setScreenshots([]);
      revokeViewBlobUrl(viewUrl);

      if (isSameOriginOrRelative(url)) {
        const fetchUrl = isMhtmlViewUrl(url)
          ? url.replace('/view_mhtml/', '/raw_page/')
          : url;
        const response = await fetch(fetchUrl, { headers: buildAuthHeaders() });
        if (!response.ok) throw new Error('Не удалось загрузить протокол');

        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : data.screenshots || [];
          const blobs = await Promise.all(
            list.map(async (path) => {
              const blob = await fetchBlobWithAuth(path);
              return { url: URL.createObjectURL(blob), name: path };
            })
          );
          setScreenshots(blobs);
          setViewUrl('');
        } else {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(
            new Blob([blob], { type: contentType || 'text/html; charset=utf-8' })
          );
          setViewUrl(objectUrl);
        }
      } else {
        setViewUrl(url);
      }
    } catch (err) {
      message.error(getTranslatedError(err, 'Ошибка при загрузке протокола'));
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownload = async (taskId, downloadUrl) => {
    try {
      if (downloadUrl) {
        // Всегда используем относительный путь через бэкенд
        const fullUrl = downloadUrl.startsWith('http')
          ? downloadUrl
          : downloadUrl; // Если это относительный путь, используем как есть (через бэкенд)
        
        const response = await fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        
        if (!response.ok) throw new Error('Ошибка при скачивании');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `protocol_${taskId.replace(/\//g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback на старый метод
        const blob = await flaskServiceAPI.downloadResult(taskId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `protocol_${taskId.replace(/\//g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      message.success('Файл успешно скачан');
    } catch (err) {
      message.error('Ошибка при скачивании файла: ' + getTranslatedError(err));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      processing: 'primary',
      failed: 'danger',
      pending: 'warning',
    };
    const labels = {
      completed: 'Завершено',
      processing: 'В обработке',
      failed: 'Ошибка',
      pending: 'Ожидание',
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getTaskTypeLabel = (type) => {
    if (!type) return '—';
    const labels = {
      image: '🖼️ Изображение',
      text: '📝 Текст',
      screenshot: '📷 Скриншот',
      generate_from_screenshot: '📷 Скриншот',
    };
    return labels[type] || type;
  };

  // Мемоизированная функция форматирования
  const formatDate = React.useCallback((dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }, []);

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Для поиска не сбрасываем страницу сразу (debounce), для остальных - сразу
    if (key !== 'search') {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: '',
      userId: selectedUserId || '', // Сохраняем фильтр по пользователю, если он передан
      taskType: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {selectedUserId ? 'Протоколы выбранного пользователя' : 'Все протоколы'}
            </h5>
            <div className="d-flex gap-2">
              {selectedUserId && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, userId: '' }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                    setUserFilterManuallyCleared(true);
                  }}
                  title="Показать всех пользователей"
                >
                  Показать всех
                </Button>
              )}
              {userFilterManuallyCleared && selectedUserId && (
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, userId: selectedUserId }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                    setUserFilterManuallyCleared(false);
                  }}
                  title="Вернуть фильтр по выбранному пользователю"
                >
                  Только выбранный
                </Button>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadTasks}
                disabled={loading}
              >
                <ReloadOutlined /> Обновить
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-3">
          {/* Фильтры */}
          <Row className="mb-3 g-2">
            <Col md={3}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <SearchOutlined />
                </InputGroup.Text>
                <FormControl
                  placeholder="Поиск по ID или номеру протокола"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Все статусы</option>
                <option value="completed">Завершено</option>
                <option value="processing">В обработке</option>
                <option value="failed">Ошибка</option>
                <option value="pending">Ожидание</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.taskType}
                onChange={(e) => handleFilterChange('taskType', e.target.value)}
              >
                <option value="">Все типы</option>
                <option value="image">По изображению</option>
                <option value="text">По тексту</option>
                <option value="screenshot">Скриншот</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="created_at">По дате создания</option>
                <option value="completed_at">По дате завершения</option>
                <option value="protocol_number">По номеру протокола</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="desc">По убыванию</option>
                <option value="asc">По возрастанию</option>
              </Form.Select>
            </Col>
            <Col md={1}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleResetFilters}
                title="Сбросить фильтры"
              >
                <FilterOutlined />
              </Button>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Протокол</th>
                      <th>URL</th>
                      <th>Комментарий</th>
                      <th>Пользователь</th>
                      <th>Тип</th>
                      <th>Статус</th>
                      <th>Создано</th>
                      <th>Завершено</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center text-muted py-3">
                          Задачи не найдены
                        </td>
                      </tr>
                    ) : (
                      tasks.map((task) => (
                        <tr key={task.id}>
                          <td style={{ fontSize: '12px' }}>{task.id}</td>
                          <td>
                            {task.protocol_number ? (
                              <Badge bg="info" style={{ fontSize: '11px' }}>
                                №{task.protocol_number}
                              </Badge>
                            ) : (
                              '—'
                            )}
                            {task.protocol_date && (
                              <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                {task.protocol_date}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(() => {
                              const url = task.request_data?.url || task.url;
                              if (!url) return '—';
                              return (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={url}
                                  style={{ display: 'inline-block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {url}
                                </a>
                              );
                            })()}
                          </td>
                          <td style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={task.comment || ''}>
                            {task.comment || '—'}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {task.user ? (
                              <div>
                                <div>{task.user.login || '—'}</div>
                                {task.user.name && (
                                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                    {task.user.name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {getTaskTypeLabel(task.task_type || task.taskType || task.type)}
                          </td>
                          <td>{getStatusBadge(task.status)}</td>
                          <td style={{ fontSize: '12px' }}>{formatDate(task.created_at)}</td>
                          <td style={{ fontSize: '12px' }}>
                            {task.completed_at ? formatDate(task.completed_at) : '—'}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleViewDetails(task)}
                                title="Просмотреть детали"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                              >
                                <EyeOutlined />
                              </Button>
                              {task.result?.download_url && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleDownload(task.id, task.result.download_url)}
                                  title="Скачать протокол"
                                  style={{ fontSize: '11px', padding: '4px 8px' }}
                                >
                                  <DownloadOutlined />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(1)}
                    />
                    <Pagination.Prev
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    />
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, idx) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = idx + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + idx;
                      } else {
                        pageNum = pagination.page - 2 + idx;
                      }
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    />
                    <Pagination.Last
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.totalPages)}
                    />
                  </Pagination>
                </div>
              )}

              {pagination.total > 0 && (
                <div className="text-center text-muted mt-2" style={{ fontSize: '12px' }}>
                  Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Модальное окно с деталями задачи */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Детали протокола</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask ? (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>ID задачи:</strong> {selectedTask.id}
                </Col>
                <Col md={6}>
                  <strong>Статус:</strong> {getStatusBadge(selectedTask.status)}
                </Col>
              </Row>
              
              {selectedTask.protocol_number && (
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Номер протокола:</strong> №{selectedTask.protocol_number}
                  </Col>
                  <Col md={6}>
                    <strong>Дата протокола:</strong> {selectedTask.protocol_date || '—'}
                  </Col>
                </Row>
              )}

              <Row className="mb-3">
                <Col md={12}>
                  <strong>URL:</strong> {selectedTask.url || '—'}
                </Col>
              </Row>

              {selectedTask.user && (
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Пользователь:</strong> {selectedTask.user.login || '—'}
                  </Col>
                  <Col md={6}>
                    <strong>Имя:</strong> {selectedTask.user.name || '—'}
                  </Col>
                  {selectedTask.user.email && (
                    <Col md={6} className="mt-2">
                      <strong>Email:</strong> {selectedTask.user.email}
                    </Col>
                  )}
                </Row>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Тип задачи:</strong> {selectedTask.task_type || '—'}
                </Col>
                <Col md={6}>
                  <strong>Создано:</strong> {formatDate(selectedTask.created_at)}
                </Col>
                {selectedTask.completed_at && (
                  <Col md={6} className="mt-2">
                    <strong>Завершено:</strong> {formatDate(selectedTask.completed_at)}
                  </Col>
                )}
              </Row>

              {selectedTask.result && (
                <div className="mb-3">
                  <strong>Ссылки:</strong>
                  <div className="mt-2 d-flex flex-column gap-2">
                    {selectedTask.result.download_url && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleDownload(selectedTask.id, selectedTask.result.download_url)}
                        style={{ fontSize: '12px' }}
                      >
                        <DownloadOutlined /> Скачать протокол (PDF/DOCX)
                      </Button>
                    )}
                    {selectedTask.result.mhtml_view_url && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                      onClick={() => handleViewProtocol(selectedTask.result.mhtml_view_url, { title: 'Просмотр страницы из веб-архива' })}
                        style={{ fontSize: '12px' }}
                      >
                        <EyeOutlined /> Просмотреть MHTML
                      </Button>
                    )}
                    {selectedTask.result.mhtml_download_url && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleDownload(selectedTask.id, selectedTask.result.mhtml_download_url)}
                        style={{ fontSize: '12px' }}
                      >
                        <DownloadOutlined /> Скачать MHTML
                      </Button>
                    )}
                    {selectedTask.result.screenshots_list_url && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                      onClick={() => handleViewProtocol(selectedTask.result.screenshots_list_url, { title: 'Просмотр скриншотов' })}
                        style={{ fontSize: '12px' }}
                      >
                        <EyeOutlined /> Просмотреть скриншоты
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {selectedTask.error && (
                <Alert variant="danger" className="mt-3">
                  <strong>Ошибка:</strong> {translateError(selectedTask.error, selectedTask.error)}
                </Alert>
              )}
            </div>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно для просмотра протокола */}
      <Modal
        show={showViewModal}
        onHide={() => {
          if (viewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(viewUrl);
          }
          screenshots.forEach((item) => {
            if (item.url?.startsWith('blob:')) {
              URL.revokeObjectURL(item.url);
            }
          });
          setScreenshots([]);
          setZoomedShot(null);
          setShowViewModal(false);
          setViewUrl('');
          setViewLoading(false);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{viewTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh' }}>
          {viewLoading ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : screenshots.length > 0 ? (
            <div style={{ padding: 16, display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(520px, 1fr))', alignItems: 'start', justifyItems: 'center' }}>
              {screenshots.map((shot) => (
                <div
                  key={shot.url}
                  style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fafafa', cursor: 'zoom-in', width: '100%', maxWidth: 720 }}
                  onClick={() => setZoomedShot(shot.url)}
                  title="Кликните для увеличения"
                >
                  <img
                    src={shot.url}
                    alt={shot.name || 'screenshot'}
                    style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#666', wordBreak: 'break-all', textAlign: 'center' }}>
                    {shot.name}
                  </div>
                </div>
              ))}
            </div>
          ) : viewUrl && (
            <iframe
              src={viewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px',
              }}
              title="Просмотр протокола"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {zoomedShot && (
        <div
          onClick={() => setZoomedShot(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: getStackTopZIndex(),
            cursor: 'zoom-out'
          }}
        >
          <img
            src={zoomedShot}
            alt="screenshot"
            style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.6)' }}
          />
        </div>
      )}
    </div>
  );
};

export default TasksList;

